import type { ClientMsg, ServerMsg } from './types'

type Handler = (msg: ServerMsg) => void
type StatusHandler = (status: 'connecting' | 'online' | 'offline', detail?: string) => void

export class Connection {
  private ws: WebSocket | null = null
  private reconnectTimer: number | null = null
  private closedByUs = false
  private attempt = 0

  constructor(
    private readonly code: string,
    private readonly name: string,
    private readonly onMessage: Handler,
    private readonly onStatus: StatusHandler,
  ) {}

  private tokenKey(): string {
    return `mathwar.token.${this.code}`
  }

  get token(): string {
    return sessionStorage.getItem(this.tokenKey()) ?? ''
  }

  set token(value: string) {
    sessionStorage.setItem(this.tokenKey(), value)
  }

  connect(): void {
    this.closedByUs = false
    this.onStatus('connecting')

    const proto = location.protocol === 'https:' ? 'wss:' : 'ws:'
    const params = new URLSearchParams({ room: this.code, name: this.name })
    if (this.token) params.set('token', this.token)

    const ws = new WebSocket(`${proto}//${location.host}/ws?${params}`)
    this.ws = ws

    ws.onopen = () => {
      this.attempt = 0
      this.onStatus('online')
    }

    ws.onmessage = (ev) => {
      let msg: ServerMsg
      try {
        msg = JSON.parse(ev.data as string)
      } catch (err) {
        console.error('нечитаемое сообщение сервера', err, ev.data)
        this.onStatus('offline', 'сервер прислал нечитаемое сообщение')
        return
      }
      if (msg.t === 'welcome') this.token = msg.token
      this.onMessage(msg)
    }

    ws.onerror = () => {
      console.error('ошибка websocket-соединения')
    }

    ws.onclose = (ev) => {
      this.ws = null
      if (this.closedByUs) return

      const reason = ev.reason || `код ${ev.code}`
      if (ev.code === 1008) {
        this.onStatus('offline', reason)
        return
      }
      this.onStatus('offline', reason)
      this.scheduleReconnect()
    }
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer !== null) return
    const delay = Math.min(1000 * 2 ** this.attempt, 10000)
    this.attempt++
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null
      this.connect()
    }, delay)
  }

  send(msg: ClientMsg): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      this.onStatus('offline', 'нет соединения с сервером')
      return
    }
    this.ws.send(JSON.stringify(msg))
  }

  close(): void {
    this.closedByUs = true
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }
    this.ws?.close(1000, 'выход')
    this.ws = null
  }
}

export async function createRoom(): Promise<string> {
  const res = await fetch('/api/room', { method: 'POST' })
  if (!res.ok) throw new Error(`сервер отказал в создании комнаты: ${res.status} ${await res.text()}`)
  const body = (await res.json()) as { code: string }
  return body.code
}

export async function roomExists(code: string): Promise<boolean> {
  const res = await fetch(`/api/room/${encodeURIComponent(code)}`)
  if (res.status === 404) return false
  if (!res.ok) throw new Error(`сервер вернул ${res.status}: ${await res.text()}`)
  return true
}
